import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Button,
  Card,
  DataTable,
  DataTableColumn,
  Modal,
  Select,
  TextInput,
  ToastProvider,
  useToast
} from '../../design-system';
import { voiceApi, VoiceSession } from './voiceClient';

interface Props { storeId: string; }

const ISSUE_OPTIONS = [
  { value: 'accurate', label: 'Accurate — no issue' },
  { value: 'misheard', label: 'Misheard the request' },
  { value: 'wrong-item', label: 'Picked the wrong menu item' },
  { value: 'wrong-table', label: 'Applied to the wrong table' },
  { value: 'hallucinated-data', label: 'Made up the data' },
  { value: 'latency', label: 'Too slow' },
  { value: 'other', label: 'Other' }
];

function modeTone(mode: string): 'accent' | 'warning' | 'danger' | 'muted' {
  if (mode === 'cloud') return 'accent';
  if (mode === 'edge') return 'warning';
  if (mode === 'degraded') return 'danger';
  return 'muted';
}
function Pill({ children, tone = 'muted' as 'accent' | 'warning' | 'danger' | 'muted' }: { children: React.ReactNode; tone?: 'accent' | 'warning' | 'danger' | 'muted' }) {
  const bg = tone === 'accent' ? 'var(--bso-accent-subtle)'
    : tone === 'warning' ? 'var(--bso-warning-subtle)'
    : tone === 'danger'  ? 'var(--bso-danger-subtle)'
    : 'var(--bso-bg-raised)';
  const fg = tone === 'accent' ? 'var(--bso-accent)'
    : tone === 'warning' ? 'var(--bso-warning)'
    : tone === 'danger'  ? 'var(--bso-danger)'
    : 'var(--bso-text-secondary)';
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 'var(--bso-radius-full)', background: bg, color: fg, fontSize: 12, fontWeight: 500 }}>
      {children}
    </span>
  );
}

const Inner: React.FC<Props> = ({ storeId }) => {
  const { getIdToken } = useAuth();
  const { show } = useToast();
  const [sessions, setSessions] = React.useState<VoiceSession[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [feedbackFor, setFeedbackFor] = React.useState<VoiceSession | null>(null);
  const [rating, setRating] = React.useState('4');
  const [issue, setIssue] = React.useState('accurate');
  const [notes, setNotes] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await voiceApi.listSessions(getIdToken, storeId, 100);
      setSessions(r.sessions);
    } catch (err: any) {
      show({ tone: 'error', title: 'Failed to load sessions', description: err.message });
    } finally {
      setLoading(false);
    }
  }, [getIdToken, storeId, show]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const columns: DataTableColumn<VoiceSession>[] = [
    {
      key: 'startedAt',
      header: 'Started',
      accessor: (r) => r.startedAt ? new Date(r.startedAt).toLocaleString() : '—',
      sortable: true,
      compare: (a, b) => (a.startedAt || '').localeCompare(b.startedAt || '')
    },
    { key: 'mode', header: 'Mode', accessor: (r) => <Pill tone={modeTone(r.mode)}>{r.mode}</Pill> },
    { key: 'provider', header: 'Provider', accessor: (r) => r.provider },
    { key: 'duration', header: 'Duration', accessor: (r) => r.durationSeconds != null ? `${r.durationSeconds}s` : '—', align: 'right' },
    { key: 'tools', header: 'Tool calls', accessor: (r) => r.toolCallCount, align: 'right' },
    { key: 'items', header: 'Items added', accessor: (r) => r.itemsAddedCount, align: 'right' },
    { key: 'latency', header: 'Latency', accessor: (r) => r.totalLatencyMs != null ? `${r.totalLatencyMs}ms` : '—', align: 'right' },
    {
      key: 'errors',
      header: 'Errors',
      accessor: (r) => r.errorCount > 0 ? <Pill tone="danger">{r.errorCount}</Pill> : 0,
      align: 'right'
    },
    {
      key: 'actions',
      header: '',
      accessor: (r) => (
        <Button size="sm" variant="ghost" onClick={() => {
          setFeedbackFor(r);
          setRating('4');
          setIssue('accurate');
          setNotes('');
        }}>Flag</Button>
      ),
      align: 'right'
    }
  ];

  const handleSubmit = async () => {
    if (!feedbackFor) return;
    setSubmitting(true);
    try {
      await voiceApi.submitFeedback(getIdToken, {
        storeId,
        sessionId: feedbackFor.sessionId,
        rating: parseInt(rating, 10),
        issue,
        notes: notes.trim() || undefined
      });
      show({ tone: 'success', title: 'Feedback submitted' });
      setFeedbackFor(null);
    } catch (err: any) {
      show({ tone: 'error', title: 'Feedback failed', description: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-theme="dark" style={{ padding: 'var(--bso-space-8) var(--bso-space-10)', background: 'var(--bso-bg-base)', color: 'var(--bso-text-primary)', fontFamily: 'var(--bso-font-sans)' }}>
      <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 'var(--bso-space-6)' }}>
        <div>
          <h1 style={{ margin: '0 0 var(--bso-space-2)' }}>Voice session log</h1>
          <p style={{ color: 'var(--bso-text-secondary)', margin: 0 }}>
            Last 100 voice sessions for store {storeId}. Flag a session to send
            feedback to the voice team.
          </p>
        </div>
        <Button variant="ghost" onClick={refresh}>Refresh</Button>
      </header>

      <Card>
        <Card.Body>
          <DataTable<VoiceSession>
            columns={columns}
            rows={sessions}
            getRowKey={(r) => r.sessionId}
            pageSize={25}
            emptyState={loading ? 'Loading…' : 'No voice sessions yet. Use the Voice Reporting widget to create one.'}
          />
        </Card.Body>
      </Card>

      <Modal
        open={feedbackFor !== null}
        onClose={() => setFeedbackFor(null)}
        title="Flag voice session"
        description={feedbackFor ? `Session ${feedbackFor.sessionId} started ${feedbackFor.startedAt ? new Date(feedbackFor.startedAt).toLocaleString() : 'unknown'}` : undefined}
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setFeedbackFor(null)} disabled={submitting}>Cancel</Button>
            <Button onClick={handleSubmit} loading={submitting}>Submit feedback</Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bso-space-4)' }}>
          <Select
            label="Rating"
            value={rating}
            onChange={(e) => setRating(e.target.value)}
            options={[
              { value: '5', label: '5 — Excellent' },
              { value: '4', label: '4 — Good' },
              { value: '3', label: '3 — Usable but flawed' },
              { value: '2', label: '2 — Confused the operator' },
              { value: '1', label: '1 — Had to be manually corrected' }
            ]}
          />
          <Select
            label="Issue"
            value={issue}
            onChange={(e) => setIssue(e.target.value)}
            options={ISSUE_OPTIONS}
          />
          <TextInput
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What happened?"
          />
        </div>
      </Modal>
    </div>
  );
};

export const VoiceSessionLog: React.FC<Props> = (props) => (
  <ToastProvider>
    <Inner {...props} />
  </ToastProvider>
);
